import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistance } from 'date-fns';
import { AlertCircle, Download, FileArchive, Info, Link2, RefreshCw } from 'lucide-react';
import { partnersApi, PartnersApiError } from '../../services/partnersApi';
import { EmptyState } from '../ui/EmptyState';
import { SectionHeader } from '../ui/SectionHeader';
import { SkeletonLoader } from '../SkeletonLoader';
import { PartnerBadge } from './PartnerPhaseBadge';
import type { PartnerServerPacks } from '../../types/partners';
import { cn } from '../../utils/cn';
import { formatBytes } from '../../utils/dataControllerFormat';
import { partnerPagePath } from '../../utils/partnerLinks';
import { serverPackFileLabel, serverPackLinkLabel } from '../../utils/partnerStatus';

interface PartnerServerPacksSectionProps {
  creatorId: string;
  /** The page's clock (ms), so relative times agree across the page. */
  now: number;
  refreshKey?: number;
}

const relative = (ms: number, now: number) => formatDistance(new Date(ms), new Date(now), { addSuffix: true });

/** Tesseract serves a file only while it is available and unexpired. */
const isDownloadable = (state: string, expiresAt: number, now: number) => state === 'available' && expiresAt > now;

/**
 * Server packs the partner shared with us through Tesseract, and the upload
 * links they have open. Partners create these links themselves, so staff
 * only ever read here.
 */
export const PartnerServerPacksSection = ({ creatorId, now, refreshKey = 0 }: PartnerServerPacksSectionProps) => {
  const [data, setData] = useState<PartnerServerPacks | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ status: number; message: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await partnersApi.serverPacks(creatorId));
      setError(null);
    } catch (e) {
      setError({ status: e instanceof PartnersApiError ? e.status : 0, message: e instanceof Error ? e.message : 'Server packs could not be loaded.' });
    } finally {
      setLoading(false);
    }
  }, [creatorId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const header = (
    <SectionHeader
      title="Server packs"
      icon={FileArchive}
      count={data ? data.files.length : undefined}
      actions={
        <button type="button" className="glass-button glass-button-sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          Refresh
        </button>
      }
    />
  );

  if (loading && !data && !error) {
    return (
      <div className="space-y-3" aria-busy="true">
        {header}
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonLoader key={i} className="h-12" />
        ))}
      </div>
    );
  }

  // Tesseract carries partner context from its migration 0047; until then there is nothing to list.
  if (error?.status === 503 && !data) {
    return (
      <div className="space-y-4">
        {header}
        <div className="alert alert-info" role="status">
          <Info className="w-4 h-4" />
          <div className="min-w-0">
            <p className="font-medium">Available once Tesseract is updated</p>
            <p className="text-sm mt-0.5">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="space-y-4">
        {header}
        <EmptyState
          bare
          compact
          tone="danger"
          icon={AlertCircle}
          title="Server packs could not be loaded"
          description={error.message}
          action={
            <button type="button" className="glass-button" onClick={() => void load()}>
              Retry
            </button>
          }
        />
      </div>
    );
  }

  const files = data?.files ?? [];
  const links = data?.open_links ?? [];

  return (
    <div className="space-y-4">
      {header}
      {error && (
        <div className="alert alert-warning" role="status">
          <AlertCircle className="w-4 h-4" />
          <span>Showing the last list. {error.message}</span>
        </div>
      )}

      {files.length === 0 && links.length === 0 ? (
        <EmptyState
          bare
          compact
          icon={FileArchive}
          title="No server packs yet"
          description="Partnered Developers create an upload link themselves, with Share a server pack in their partner channel or with /transfer request. What they upload shows here, and the staff feed gets a copy."
        />
      ) : (
        <>
          {files.length > 0 && (
            <>
              <div className="hidden md:block table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="th">File</th>
                      <th className="th">Shared by</th>
                      <th className="th text-right">Downloads</th>
                      <th className="th">Expires</th>
                      <th className="th">State</th>
                      <th className="th text-right">Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {files.map((file) => {
                      const label = serverPackFileLabel(file.state, file.expires_at, now);
                      const downloadable = isDownloadable(file.state, file.expires_at, now);
                      return (
                        <tr key={file.slug}>
                          <td className="td">
                            <p className="text-sm text-white break-all">{file.filename}</p>
                            <p className="text-[11px] text-glass-500 tabular-nums">
                              {formatBytes(file.size)} · shared {relative(file.created_at, now)}
                              {file.context_label ? ` · ${file.context_label}` : ''}
                            </p>
                            {file.report_id && (
                              <Link to={partnerPagePath(creatorId, 'updates', file.report_id)} className="link text-xs">
                                For an update report
                              </Link>
                            )}
                          </td>
                          <td className="td text-sm text-glass-300">{file.uploaded_by.label}</td>
                          <td className="td text-right tabular-nums text-sm">
                            {file.download_count}
                            <span className="text-glass-500">{file.max_downloads !== null ? ` of ${file.max_downloads}` : ''}</span>
                          </td>
                          <td className="td text-sm text-glass-300 tabular-nums">{relative(file.expires_at, now)}</td>
                          <td className="td">
                            <PartnerBadge status={label} dot />
                          </td>
                          <td className="td text-right">
                            {downloadable ? (
                              <a href={file.download_url} target="_blank" rel="noopener noreferrer" className="glass-button glass-button-sm">
                                <Download className="w-4 h-4" />
                                Download
                              </a>
                            ) : (
                              <span className="text-xs text-glass-500">Not available</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden space-y-3">
                {files.map((file) => {
                  const label = serverPackFileLabel(file.state, file.expires_at, now);
                  return (
                    <div key={file.slug} className="subcard p-3 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm text-white break-all">{file.filename}</p>
                        <PartnerBadge status={label} dot />
                      </div>
                      <p className="text-xs text-glass-400 tabular-nums">
                        {formatBytes(file.size)} · by {file.uploaded_by.label} · {file.download_count}
                        {file.max_downloads !== null ? ` of ${file.max_downloads}` : ''} downloads · expires {relative(file.expires_at, now)}
                      </p>
                      {isDownloadable(file.state, file.expires_at, now) && (
                        <a href={file.download_url} target="_blank" rel="noopener noreferrer" className="glass-button glass-button-sm w-full justify-center">
                          <Download className="w-4 h-4" />
                          Download
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {links.length > 0 && (
            <div className="subcard p-3 md:p-4 space-y-2">
              <p className="eyebrow flex items-center gap-1.5">
                <Link2 className="w-3.5 h-3.5" /> Upload links still open ({links.length})
              </p>
              <ul className="divide-y divide-glass-200/10">
                {links.map((link) => (
                  <li key={link.grant_id} className="py-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm text-white">{link.context_label ?? 'Server pack upload'}</p>
                      <p className="text-xs text-glass-500 tabular-nums">
                        Created by {link.requested_by_label} {relative(link.created_at, now)} · closes {relative(link.expires_at, now)}
                      </p>
                    </div>
                    <PartnerBadge status={serverPackLinkLabel(link.state)} dot />
                  </li>
                ))}
              </ul>
              <p className="text-xs text-glass-500">Each partner may have a few links open at once. A link closes when its upload finishes or it runs out.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};
